/* eslint-disable camelcase, security/detect-object-injection */
// Camelcase + object-injection rules are suppressed at the file level because
// this section reads/writes snake_case API shapes (OrganizationWorkflowLinkOut,
// ServerEnvironmentOption) and keys an internal map by trusted UUIDs from the
// workflow definition — both are safe by construction here.
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useGetOne, useRecordContext, useTranslate } from 'react-admin'
import { useFormContext, useWatch } from 'react-hook-form'
import Box from '@mui/material/Box'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { getJson, getUrl } from '../../providers/data'
import {
  OrganizationWorkflowLinkOut,
  OrgSeatOption,
  ServerEnvironmentOption,
  WorkflowOut,
} from '../../types/apiRequests'
import { themeColor } from '../../layout/theme/color'

interface RequiredServer {
  server_id: string
  display_name: string
}

interface ServerConfigRow {
  server_id: string
  seat_id: string | null
  environment_id: string | null
  base_url: string | null
}

const deriveRequiredServers = (workflow: WorkflowOut | undefined): RequiredServer[] => {
  const seen = new Map<string, RequiredServer>()
  for (const node of workflow?.nodes ?? []) {
    const data = (node as { data?: Record<string, unknown> }).data
    if (!data || data.type !== 'tool_call') continue
    const sid = data.serverId
    if (typeof sid !== 'string' || !sid) continue
    if (seen.has(sid)) continue
    const label = (data.serverDisplayName as string) || (data.serverName as string) || sid
    seen.set(sid, { server_id: sid, display_name: label })
  }
  return Array.from(seen.values())
}

// A seat only matters for cron triggers: those run without an inbound user, so
// the org-admin pins an identity. Manual triggers act as the triggering user
// and MCP triggers pass through the caller, so the seat picker is hidden there.
const workflowHasCronTrigger = (workflow: WorkflowOut | undefined): boolean =>
  (workflow?.nodes ?? []).some((node) => {
    const data = (node as { data?: Record<string, unknown> }).data
    return data?.type === 'trigger' && (data.triggerType as string)?.trim() === 'cron'
  })

const LinkServerConfigsSection: FC = () => {
  const record = useRecordContext<OrganizationWorkflowLinkOut>()
  const translate = useTranslate()
  const { setValue, getValues } = useFormContext()

  // On create there is no record yet, so fall back to the ids the user selects
  // in the form. This lets the section render in both Edit and Create.
  const watchedWorkflowId = useWatch({ name: 'workflow_id' }) as string | undefined
  const watchedOrganizationId = useWatch({ name: 'organization_id' }) as string | undefined
  const workflowId = record?.workflow_id ?? watchedWorkflowId ?? ''
  const organizationId = record?.organization_id ?? watchedOrganizationId ?? ''

  const { data: workflow } = useGetOne<WorkflowOut>(
    'workflow',
    { id: workflowId },
    { enabled: !!workflowId },
  )

  const requiredServers = useMemo(() => deriveRequiredServers(workflow), [workflow])
  const showSeat = useMemo(() => workflowHasCronTrigger(workflow), [workflow])

  // Mirror form state via useWatch so external resets (revert, refresh) flow
  // back into our rows without going through local-state drift.
  const formValue = useWatch({ name: 'server_configs' }) as ServerConfigRow[] | undefined
  const initialFromRecord = useMemo(
    () => (record?.server_configs ?? []) as ServerConfigRow[],
    [record?.server_configs],
  )

  const rows: ServerConfigRow[] = useMemo(() => {
    const lookup = new Map<string, ServerConfigRow>()
    for (const r of formValue ?? initialFromRecord) {
      if (r && typeof r.server_id === 'string') lookup.set(r.server_id, r)
    }
    return requiredServers.map((s) => lookup.get(s.server_id) ?? {
      server_id: s.server_id,
      seat_id: null,
      environment_id: null,
      base_url: null,
    })
  }, [requiredServers, formValue, initialFromRecord])

  const [environmentsByServer, setEnvironmentsByServer] = useState<Record<string, ServerEnvironmentOption[]>>({})
  const [seats, setSeats] = useState<OrgSeatOption[]>([])

  useEffect(() => {
    if (requiredServers.length === 0) return
    const pending = requiredServers.filter((s) => !environmentsByServer[s.server_id])
    if (pending.length === 0) return
    let cancelled = false
    void Promise.all(
      pending.map(async (s) => {
        try {
          const envs = await getJson<ServerEnvironmentOption[]>(
            `${getUrl()}/workflow/server_environments/${s.server_id}`,
          )
          return [s.server_id, envs] as const
        } catch {
          return [s.server_id, [] as ServerEnvironmentOption[]] as const
        }
      }),
    ).then((entries) => {
      if (cancelled) return
      setEnvironmentsByServer((prev) => {
        const next = { ...prev }
        for (const [sid, envs] of entries) next[sid] = envs
        return next
      })
    })
    return () => {
      cancelled = true
    }
  }, [requiredServers, environmentsByServer])

  useEffect(() => {
    if (!organizationId || !showSeat) return
    let cancelled = false
    void getJson<OrgSeatOption[]>(`${getUrl()}/workflow/org_seats/${organizationId}`)
      .then((data) => {
        if (!cancelled) setSeats(data)
      })
      .catch(() => {
        if (!cancelled) setSeats([])
      })
    return () => {
      cancelled = true
    }
  }, [organizationId, showSeat])

  // Prefill base_url with the environment default, mirroring the portal request
  // flow: auto-select the environment when there is exactly one, and fill its
  // default base_url. Only fills empty base_urls so saved values are never
  // clobbered. Each server is processed once (tracked via prefilledRef).
  const prefilledRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (requiredServers.length === 0) return
    const current = (getValues('server_configs') as ServerConfigRow[] | undefined) ?? []
    const byId = new Map(current.map((r) => [r.server_id, r]))
    let changed = false
    const next = requiredServers.map((s) => {
      const existing = byId.get(s.server_id) ?? {
        server_id: s.server_id,
        seat_id: null,
        environment_id: null,
        base_url: null,
      }
      const envs = environmentsByServer[s.server_id]
      if (!envs || prefilledRef.current.has(s.server_id)) return existing
      prefilledRef.current.add(s.server_id)
      if (existing.base_url) return existing
      const envId = existing.environment_id ?? (envs.length === 1 ? envs[0].id : null)
      const env = envs.find((e) => e.id === envId)
      if (!env?.base_url) return existing
      changed = true
      return { ...existing, environment_id: envId, base_url: env.base_url }
    })
    if (changed) setValue('server_configs', next, { shouldDirty: true })
  }, [requiredServers, environmentsByServer, getValues, setValue])

  const updateRow = (server_id: string, patch: Partial<ServerConfigRow>) => {
    const next = rows.map((r) => (r.server_id === server_id ? { ...r, ...patch } : r))
    setValue('server_configs', next, { shouldDirty: true })
  }

  if (!workflowId) return null

  return (
    <Box sx={{ width: '100%', mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, mb: 0.75 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: themeColor.slate, letterSpacing: '-0.01em' }}>
          {translate('resources.organization_workflow_link.serverConfigsTitle')}
        </Typography>
        {requiredServers.length > 0 && (
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: themeColor.slate60,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {requiredServers.length}
          </Typography>
        )}
      </Box>
      <Typography sx={{ fontSize: 12, color: themeColor.slate60, mb: 1.5, lineHeight: 1.5 }}>
        {translate('resources.organization_workflow_link.serverConfigsSubtitle')}
      </Typography>

      {requiredServers.length === 0 ? (
        <Box
          sx={{
            background: 'rgba(0,0,0,0.02)',
            borderRadius: '10px',
            border: '1px dashed rgba(0,0,0,0.10)',
            p: 2,
          }}
        >
          <Typography sx={{ fontSize: 12, color: themeColor.slate, lineHeight: 1.55 }}>
            {translate('resources.organization_workflow_link.serverConfigsEmpty')}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {rows.map((row) => {
            const server = requiredServers.find((s) => s.server_id === row.server_id)
            const envs = environmentsByServer[row.server_id] ?? []
            return (
              <Box
                key={row.server_id}
                sx={{
                  p: 2,
                  borderRadius: '10px',
                  border: '1px solid rgba(0,0,0,0.06)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                }}
              >
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: themeColor.slate }}>
                  {server?.display_name ?? row.server_id}
                </Typography>
                <TextField
                  select
                  label={translate('resources.organization_workflow_link.environment')}
                  value={row.environment_id ?? ''}
                  onChange={(e) => {
                    const envId = e.target.value || null
                    const env = envs.find((x) => x.id === envId)
                    updateRow(row.server_id, {
                      environment_id: envId,
                      base_url: env?.base_url ?? row.base_url,
                    })
                  }}
                  size="small"
                  fullWidth
                >
                  <MenuItem value="">
                    <em>{translate('resources.organization_workflow_link.environmentNone')}</em>
                  </MenuItem>
                  {envs.map((env) => (
                    <MenuItem key={env.id} value={env.id}>
                      {env.display_name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label={translate('resources.organization_workflow_link.baseUrl')}
                  value={row.base_url ?? ''}
                  onChange={(e) => updateRow(row.server_id, { base_url: e.target.value || null })}
                  size="small"
                  fullWidth
                  placeholder="https://api.example.com"
                />
                {showSeat && (
                  <TextField
                    select
                    label={translate('resources.organization_workflow_link.seat')}
                    value={row.seat_id ?? ''}
                    onChange={(e) => updateRow(row.server_id, { seat_id: e.target.value || null })}
                    size="small"
                    fullWidth
                    helperText={translate('resources.organization_workflow_link.seatHelper')}
                  >
                    <MenuItem value="">
                      <em>{translate('resources.organization_workflow_link.seatNone')}</em>
                    </MenuItem>
                    {seats.map((seat) => (
                      <MenuItem key={seat.id} value={seat.id}>
                        {seat.auth0_email}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Box>
            )
          })}
        </Box>
      )}
    </Box>
  )
}

export default LinkServerConfigsSection
